/**
 * SpeakerView — facilitatorns talarstöd.
 * Visar ett block i taget med stödord, löptext (toggle), och navigation.
 * Skriver currentBlock till Firestore → publik skärm uppdateras i realtid.
 * Tangentnavigering: ← / → eller ↑ / ↓.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Chip, Collapse, Tooltip, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TvIcon from '@mui/icons-material/Tv';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TimerIcon from '@mui/icons-material/Timer';
import { db } from '../../../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import TimerOverlay from '../../../../components/TimerOverlay';
import DisplayModeBar from '../DisplayModeBar';

const accent = '#E05500';

export default function SpeakerView({ sessionId }) {
  const navigate = useNavigate();
  const [intro,        setIntro]       = useState(null);
  const [session,      setSession]     = useState(null);
  const wifiSsid     = session?.wifiSsid || '';
  const wifiPassword = session?.wifiPassword || '';
  const [loading,      setLoading]     = useState(true);
  const [showText,     setShowText]    = useState(true);
  const [activeTimer,  setActiveTimer] = useState(null);
  const [displayMode,  setDisplayMode] = useState('blank');

  const currentBlock = intro?.currentBlock ?? 0;
  const blocks = intro?.blocks || [];
  const block = blocks[currentBlock];

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setIntro(d.intro || null);
        setSession(d);
        setDisplayMode(d.display?.mode || 'blank');
      }
      setLoading(false);
    });
    return unsub;
  }, [sessionId]);

  const goTo = useCallback(async (idx) => {
    if (!blocks.length || idx < 0 || idx >= blocks.length) return;
    setShowText(false);
    await updateDoc(doc(db, 'workshops', sessionId), { 'intro.currentBlock': idx });
  }, [sessionId, blocks.length]);

  // Keyboard navigation
  useEffect(() => {
    const handle = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(currentBlock + 1);
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   goTo(currentBlock - 1);
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [currentBlock, goTo]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (!intro || blocks.length === 0) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, p: 4 }}>
      <Typography color="text.secondary" textAlign="center" maxWidth={400}>
        Inget talarstöd är skapat ännu.<br />
        Gå tillbaka till workshopens inställningar och skapa ett intro.
      </Typography>
      <Button variant="outlined" onClick={() => navigate(`/workshopn/${sessionId}/intro`)}
        sx={{ textTransform: 'none', borderColor: accent, color: accent }}>
        Skapa talarstöd
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', display: 'flex', flexDirection: 'column' }}>

      {/* Top bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 3, py: 1.25,
        borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', gap: 2,
      }}>
        <Typography fontSize="0.65rem" fontWeight={700} color="text.disabled" letterSpacing={1.5}>
          TALARSTÖD
        </Typography>
        <Box sx={{ flex: 1 }} />

        <Tooltip title="Piltangenter ← → byter block">
          <KeyboardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Tooltip>

        <Button size="small" variant="contained" startIcon={<TvIcon sx={{ fontSize: 15 }} />}
          onClick={async () => {
            await updateDoc(doc(db, 'workshops', sessionId), { 'display.mode': 'intro' });
            const sw = window.screen.width, sh = window.screen.height;
            window.open(`/workshopn/${sessionId}/display`, 'workshop-display',
              `width=${sw},height=${sh},left=${sw},top=0,menubar=no,toolbar=no,location=no,status=no`);
          }}
          sx={{ textTransform: 'none', fontSize: '0.72rem', bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, px: 1.5 }}>
          Öppna HDMI-display
        </Button>
        <Button size="small" variant="outlined" startIcon={<TvIcon sx={{ fontSize: 15 }} />}
          onClick={() => window.open(`/workshopn/${sessionId}/schedule-speaker`, '_blank')}
          sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: '#42A5F5', color: '#42A5F5', px: 1.5 }}>
          Kör körschema
        </Button>

        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          onClick={() => navigate(`/workshopn/${sessionId}/intro`)}
          sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: 'divider', color: 'text.secondary', px: 1.5 }}>
          Redigera
        </Button>

        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
          {blocks.map((_, i) => (
            <Box key={i} onClick={() => goTo(i)}
              sx={{
                width: i === currentBlock ? 18 : 7, height: 7,
                borderRadius: 4,
                bgcolor: i === currentBlock ? accent : 'rgba(255,255,255,0.18)',
                cursor: 'pointer', transition: 'all 0.2s',
                '&:hover': { bgcolor: i === currentBlock ? accent : 'rgba(255,255,255,0.35)' },
              }} />
          ))}
          <Typography fontSize="0.65rem" color="text.disabled" ml={0.75}>
            {currentBlock + 1}/{blocks.length}
          </Typography>
        </Box>
      </Box>

      {/* Display mode bar */}
      <DisplayModeBar sessionId={sessionId} currentMode={displayMode} />

      {/* Main content */}
      <Box sx={{ flex: 1, maxWidth: 780, mx: 'auto', width: '100%', p: { xs: 3, md: 4 } }}>

        {/* Section label */}
        <Typography
          fontSize="0.7rem" fontWeight={800} color={accent}
          letterSpacing={2} mb={1.5} textTransform="uppercase"
        >
          {block.label || `Block ${currentBlock + 1}`}
        </Typography>

        {/* Keywords */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {(block.keywords || []).map((kw, i) => (
            <Chip key={i} label={kw}
              sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'text.primary',
                fontWeight: 700,
                fontSize: '1rem',
                height: 38,
                border: '1px solid rgba(255,255,255,0.14)',
                px: 0.5,
                letterSpacing: '0.01em',
              }} />
          ))}
        </Box>

        {/* Speaker text */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="text" size="small"
            onClick={() => setShowText(v => !v)}
            endIcon={showText ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.78rem', p: 0, mb: 0.75 }}
          >
            {showText ? 'Dölj löptext' : 'Visa löptext'}
          </Button>
          <Collapse in={showText}>
            <Box sx={{ p: 2.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary" lineHeight={1.75} fontSize="0.93rem">
                {block.speakerText || <em>Ingen löptext.</em>}
              </Typography>
            </Box>
          </Collapse>
        </Box>

        {/* Public screen preview */}
        <Box sx={{
          p: 2, borderRadius: 1.5,
          bgcolor: 'rgba(255,255,255,0.025)',
          border: '1px solid rgba(255,255,255,0.07)',
        }}>
          <Typography fontSize="0.62rem" color="text.disabled" mb={1} letterSpacing={1} fontWeight={600}>
            STORSKÄRMEN VISAR NU
          </Typography>
          <Typography fontWeight={600} fontSize="1.1rem" color="text.primary" lineHeight={1.3}>
            {block.publicTitle || <em style={{ opacity: 0.4 }}>Ingen rubrik</em>}
          </Typography>
          {block.publicQuote && (
            <Typography fontSize="0.88rem" color={accent} fontStyle="italic" mt={0.75}>
              "{block.publicQuote}"
            </Typography>
          )}
        </Box>

        {/* WiFi-info (block 0 = välkomstsliden) */}
        {currentBlock === 0 && wifiSsid && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontSize="0.62rem" color="text.disabled" letterSpacing={1} fontWeight={600}>
              WIFI
            </Typography>
            <Typography fontSize="0.88rem" fontWeight={700} color="text.primary">{wifiSsid}</Typography>
            {wifiPassword && (
              <>
                <Typography fontSize="0.7rem" color="text.disabled">·</Typography>
                <Typography fontSize="0.88rem" color="text.secondary" fontFamily="monospace">{wifiPassword}</Typography>
              </>
            )}
          </Box>
        )}

        {/* Körschema – current block + sub-items with timers */}
        {(() => {
          const scheduleBlocks = session?.schedule?.blocks || [];
          const schedBlk = scheduleBlocks[session?.schedule?.currentBlock ?? 0];
          if (!schedBlk) return null;
          return (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: schedBlk.subItems?.length ? 1.25 : 0 }}>
                <Typography fontSize="0.62rem" color="text.disabled" letterSpacing={1} fontWeight={600} sx={{ flex: 1 }}>
                  KÖRSCHEMA — {schedBlk.time || ''}
                </Typography>
                {schedBlk.duration && (
                  <Tooltip title={`Starta timer: ${schedBlk.duration} min`}>
                    <IconButton size="small"
                      onClick={() => setActiveTimer({ label: schedBlk.title, totalSeconds: schedBlk.duration * 60 })}
                      sx={{ color: accent, p: 0.5, '&:hover': { bgcolor: accent + '22' } }}>
                      <TimerIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography fontSize="0.78rem" fontWeight={600} color="text.primary" ml={1}>
                  {schedBlk.title}
                </Typography>
              </Box>
              {schedBlk.subItems?.length > 0 && (
                <Box sx={{ pl: 1.5, borderLeft: `2px solid ${accent}33`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {schedBlk.subItems.map((sub, si) => (
                    <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="0.7rem" color="text.disabled" sx={{ minWidth: 40, fontFamily: 'monospace' }}>
                        {sub.duration} min
                      </Typography>
                      <Typography fontSize="0.78rem" color="text.secondary" sx={{ flex: 1 }}>
                        {sub.text}
                      </Typography>
                      <Tooltip title={`Timer: ${sub.duration} min`}>
                        <IconButton size="small"
                          onClick={() => setActiveTimer({ label: sub.text, totalSeconds: sub.duration * 60 })}
                          sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: accent } }}>
                          <TimerIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>

      {/* Timer overlay */}
      {activeTimer && (
        <TimerOverlay
          open
          label={activeTimer.label}
          totalSeconds={activeTimer.totalSeconds}
          color={accent}
          onClose={() => setActiveTimer(null)}
        />
      )}

      {/* Navigation footer */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 2,
        borderTop: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', gap: 2,
      }}>
        <Button
          variant="outlined" startIcon={<ArrowBackIcon />}
          disabled={currentBlock === 0}
          onClick={() => goTo(currentBlock - 1)}
          sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: accent, color: accent }, minWidth: 130 }}
        >
          Föregående
        </Button>

        {/* Block name thumbnails */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', flex: 1, mx: 2 }}>
          {blocks.map((b, i) => (
            <Box key={i} onClick={() => goTo(i)}
              sx={{
                px: 1.25, py: 0.35, borderRadius: 1,
                fontSize: '0.62rem', fontWeight: 600,
                bgcolor: i === currentBlock ? accent : 'rgba(255,255,255,0.05)',
                color: i === currentBlock ? '#fff' : 'text.disabled',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: i === currentBlock ? accent : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: i === currentBlock ? accent : 'rgba(255,255,255,0.1)' },
                whiteSpace: 'nowrap',
              }}
            >
              {b.label || `${i + 1}`}
            </Box>
          ))}
        </Box>

        <Button
          variant="contained" endIcon={<ArrowForwardIcon />}
          disabled={currentBlock === blocks.length - 1}
          onClick={() => goTo(currentBlock + 1)}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, minWidth: 130 }}
        >
          Nästa
        </Button>
      </Box>
    </Box>
  );
}
